import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { type AgreementKey, type AgreementPostRequestBody, getAgreement, postAgreement } from '@/services/api/profile/agreement';

type ProfileAgreementMutationBody = {
  key: AgreementKey;
  body?: AgreementPostRequestBody;
};

const profileAgreementQueryKey = (pid: UserPid, key: AgreementKey) => ['profile', 'agreement', pid, key];

const profileAgreementQueryOptions = (pid: UserPid, key: AgreementKey) =>
  queryOptions({
    queryFn: () => getAgreement(key),
    queryKey: profileAgreementQueryKey(pid, key),
  });

const useProfileAgreement = (key: AgreementKey) => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();
  const queryResult = useQuery(profileAgreementQueryOptions(pid, key));

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: profileAgreementQueryKey(pid, key) });
  }, [key, pid, queryClient]);

  return {
    ...queryResult,
    invalidate,
  };
};

const useProfileAgreementMutation = () => {
  return useMutation<unknown, Error, ProfileAgreementMutationBody>({
    mutationFn: ({ key, body }) => postAgreement(key, body),
  });
};

export { useProfileAgreement, useProfileAgreementMutation };
